// Motion enhances an already complete page. No content depends on JavaScript to be visible.
const motionPage = document.querySelector<HTMLElement>('[data-motion]')
if (motionPage) {
  const root = motionPage
  const mode = root.dataset.motion
  const reduce = matchMedia('(prefers-reduced-motion: reduce)')
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)')
  const toggle = root.querySelector<HTMLButtonElement>('.motion-toggle')!
  const scene = root.querySelector<HTMLElement>('.possibility-scene')!
  const stamp = root.querySelector<HTMLElement>('.purpose-stamp')!
  const flower = root.querySelector<HTMLElement>('.end-flower')!
  const cards = [...root.querySelectorAll<HTMLElement>('.story-block')]
  const chapters = [...root.querySelectorAll<HTMLElement>('.studio-chapter-index span')]
  const animations = new Set<Animation>()
  const revealed = new WeakSet<Element>()
  const targets = [...root.querySelectorAll<HTMLElement>(
    '.possibility-purpose > .eyebrow, .possibility-purpose > h2, .possibility-purpose > p:last-child, ' +
    '.possibility-section-heading > *, .story-block, .possibility-principles > div:first-child, ' +
    '.principle-questions details, .possibility-end .eyebrow, .possibility-end h2, .possibility-end .concept-actions, ' +
    '.foot-brand, .foot-cols > div, .foot-bottom',
  )]
  let paused = false
  let frame = 0
  let observer: IntersectionObserver | undefined
  let heroVisible = true
  let x = 0, y = 0, nextX = 0, nextY = 0
  const enabled = () => !paused && !reduce.matches
  const ease = 'cubic-bezier(.22, 1, .36, 1)'

  function animate(element: Element, keyframes: Keyframe[], duration = 950, delay = 0) {
    if (!enabled()) return
    const animation = element.animate(keyframes, { duration, delay, easing: ease, fill: 'backwards' })
    animations.add(animation)
    animation.finished.then(() => animations.delete(animation), () => animations.delete(animation))
  }

  function reveal(element: HTMLElement, delay = 0) {
    if (revealed.has(element)) return
    revealed.add(element)
    element.style.removeProperty('opacity')
    if (!enabled()) return
    const first: Keyframe = mode === 'orbit'
      ? { opacity: 0, translate: '0 26px', scale: '.96', filter: 'blur(6px)' }
      : mode === 'studio'
        ? { opacity: 0, translate: '0 45px', rotate: '-1deg' }
        : { opacity: 0, translate: '0 34px' }
    animate(element, [first, { opacity: 1, translate: '0 0', scale: '1', rotate: '0deg', filter: 'blur(0)' }], mode === 'orbit' ? 1100 : 900, delay)
    if (element.matches('.story-block')) {
      const pieces = element.querySelectorAll('.memory-illustration > *, .project-illustration > *, .mission-illustration > *')
      pieces.forEach((piece, index) => animate(piece, [
        { opacity: 0, translate: '0 20px', scale: '.88' },
        { opacity: 1, translate: '0 0', scale: '1' },
      ], 800, delay + 180 + index * 95))
    }
  }

  function clearMotion() {
    observer?.disconnect()
    animations.forEach((animation) => animation.cancel())
    animations.clear()
    targets.forEach((element) => { element.style.removeProperty('opacity'); revealed.add(element) })
    cancelAnimationFrame(frame)
    frame = 0
    scene.style.removeProperty('transform')
    root.style.removeProperty('--scene-shift')
    root.style.removeProperty('--scene-turn')
    stamp.style.removeProperty('--stamp-turn')
    flower.style.removeProperty('--flower-turn')
    x = y = nextX = nextY = 0
  }

  function initialize() {
    if (!enabled()) return
    observer = new IntersectionObserver((entries) => {
      let delay = 0
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        reveal(entry.target as HTMLElement, delay)
        delay += 90
        observer?.unobserve(entry.target)
      })
    }, { threshold: 0, rootMargin: '0px 0px -7% 0px' })
    targets.forEach((element) => {
      if (revealed.has(element)) return
      // Content already above the viewport stays visible on back navigation / anchor entry.
      if (element.getBoundingClientRect().bottom <= 0) { revealed.add(element); return }
      observer!.observe(element)
      element.style.opacity = '0'
    })
  }

  // Only event-driven frames run. There is no perpetual animation or idle render loop.
  function tick() {
    frame = 0
    if (document.hidden) return
    const distance = document.documentElement.scrollHeight - innerHeight
    root.style.setProperty('--reading-progress', String(distance > 0 ? Math.max(0, Math.min(1, scrollY / distance)) : 0))
    if (chapters.length) {
      let nearest = 0
      let nearestDistance = Infinity
      cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().top - innerHeight * .3)
        if (distance < nearestDistance) { nearestDistance = distance; nearest = index }
      })
      chapters.forEach((chapter, index) => chapter.classList.toggle('is-current', index === nearest))
    }
    if (!enabled()) return
    const stampBox = stamp.getBoundingClientRect()
    if (stampBox.bottom > 0 && stampBox.top < innerHeight) stamp.style.setProperty('--stamp-turn', `${(1 - stampBox.top / innerHeight) * (mode === 'studio' ? -18 : 55)}deg`)
    const flowerBox = flower.getBoundingClientRect()
    if (flowerBox.bottom > 0 && flowerBox.top < innerHeight) flower.style.setProperty('--flower-turn', `${(1 - flowerBox.top / innerHeight) * 65}deg`)
    if (heroVisible) {
      const progress = Math.min(1, Math.max(0, scrollY / (scene.offsetTop + scene.offsetHeight)))
      root.style.setProperty('--scene-shift', `${-progress * (mode === 'paper' ? 24 : 14)}px`)
      root.style.setProperty('--scene-turn', `${progress * (mode === 'orbit' ? 90 : 6)}deg`)
      x += (nextX - x) * .13
      y += (nextY - y) * .13
      scene.style.transform = `perspective(1000px) rotateX(${-y * 3}deg) rotateY(${x * 3}deg)`
      if (Math.abs(nextX - x) + Math.abs(nextY - y) > .005) schedule()
    }
  }
  function schedule() { if (!frame && !document.hidden) frame = requestAnimationFrame(tick) }

  // Reveal focused content immediately, including a keyboard jump to an offscreen link.
  root.addEventListener('focusin', (event) => {
    const focused = event.target as Element
    targets.forEach((target) => {
      if (!target.contains(focused)) return
      reveal(target)
      target.getAnimations().forEach((animation) => animation.finish())
    })
  })
  root.querySelectorAll('details').forEach((detail) => detail.addEventListener('toggle', () => {
    if (detail.open) animate(detail.querySelector('p')!, [{ opacity: 0, translate: '0 -8px' }, { opacity: 1, translate: '0 0' }], 400)
    schedule()
  }))
  scene.addEventListener('pointermove', (event) => {
    if (!enabled() || !finePointer.matches || event.pointerType !== 'mouse') return
    const box = scene.getBoundingClientRect()
    nextX = ((event.clientX - box.left) / box.width - .5) * 2
    nextY = ((event.clientY - box.top) / box.height - .5) * 2
    schedule()
  })
  scene.addEventListener('pointerleave', () => { nextX = nextY = 0; schedule() })
  new IntersectionObserver(([entry]) => {
    heroVisible = entry.isIntersecting
    if (!heroVisible) { nextX = nextY = x = y = 0; scene.style.removeProperty('transform') }
    schedule()
  }).observe(scene)
  addEventListener('scroll', schedule, { passive: true })
  addEventListener('resize', schedule, { passive: true })
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(frame); frame = 0
      animations.forEach((animation) => animation.pause())
    } else {
      if (enabled()) animations.forEach((animation) => animation.play())
      schedule()
    }
  })
  toggle.hidden = reduce.matches
  toggle.addEventListener('click', () => {
    paused = !paused
    root.toggleAttribute('data-motion-paused', paused)
    toggle.setAttribute('aria-pressed', String(paused))
    toggle.setAttribute('aria-label', paused ? 'Resume motion' : 'Pause motion')
    toggle.firstElementChild!.textContent = paused ? '▷' : 'Ⅱ'
    if (paused) clearMotion()
    else initialize()
    schedule()
  })
  reduce.addEventListener('change', () => {
    toggle.hidden = reduce.matches
    if (reduce.matches) clearMotion()
    else initialize()
    schedule()
  })
  initialize()
  if (enabled()) {
    animate(root.querySelector('.nav')!, [{ opacity: 0, translate: '0 -12px' }, { opacity: 1, translate: '0 0' }], 800)
    root.querySelectorAll('.possibility-hero-copy > .eyebrow, .hero-line, .possibility-hero-copy > p:not(.eyebrow), .possibility-hero-copy > a').forEach((element, index) => {
      animate(element, [{ opacity: 0, translate: '0 28px', filter: 'blur(4px)' }, { opacity: 1, translate: '0 0', filter: 'blur(0)' }], 1000, index * 100)
    })
    const selector = mode === 'orbit' ? '.orbit-core, .orbit-node, .orbit-halo, .orbit-dot' : mode === 'studio' ? '.studio-card, .studio-tape, .studio-pencil' : '.scene-folder, .scene-note, .scene-ticket, .scene-spark'
    scene.querySelectorAll<HTMLElement>(selector).forEach((element, index) => {
      const start: Keyframe = { opacity: 0, translate: `0 ${mode === 'studio' ? '65' : '32'}px`, scale: mode === 'orbit' ? '.65' : '.94' }
      if (mode === 'orbit' && element.matches('.orbit-node')) {
        const art = element.parentElement!
        start.translate = `${art.clientWidth / 2 - element.offsetLeft - element.offsetWidth / 2}px ${art.clientHeight / 2 - element.offsetTop - element.offsetHeight / 2}px`
      } else if (mode === 'paper') {
        start.rotate = element.matches('.scene-note') ? '1 0 0 -35deg' : element.matches('.scene-spark') ? '-90deg' : '-8deg'
      } else if (mode === 'studio') {
        start.rotate = `${index % 2 ? 12 : -12}deg`
      }
      animate(element, [start, { opacity: 1, translate: '0 0', scale: '1', rotate: '0deg' }], 1200, 150 + index * 100)
    })
    scene.querySelectorAll('.orbit-lines path').forEach((element) => animate(element, [{ strokeDasharray: '400', strokeDashoffset: '400' }, { strokeDasharray: '400', strokeDashoffset: '0' }], 1600, 250))
  }
  schedule()
}
